import { makeAutoObservable } from 'mobx';
import { FEATURES } from './feature.constants';
import React from 'react';
import { IFeature } from 'common/services/feature-service/feature-service.types';
import featureService from 'common/services/feature-service/feature.service';
import { FeatureName } from 'common/enums/feature-name.enum';

class FeatureStore {
  features: IFeature[] = [];

  constructor() {
    makeAutoObservable(this);
    this.restoreFeatures();
  }

  getByName(name: FeatureName) {
    return this.features.find((i) => i.name === name);
  }

  toggleFeature(id: string, isEnabled: boolean) {
    const index = this.features.findIndex((i) => i.id === id);
    if (index !== -1) {
      this.features[index].isEnabled = isEnabled;
      this.setFeatures(this.features);
    }
  }

  setFeatures(features: IFeature[]) {
    localStorage.setItem(FEATURES, JSON.stringify(features));
    this.features = features;
  }

  restoreFeatures() {
    const source = localStorage.getItem(FEATURES);
    this.features = source ? JSON.parse(source) : [];
  }

  clearFeatures() {
    localStorage.removeItem(FEATURES);
    this.features = [];
  }

  async requestFeatures() {
    const features = await featureService.getFeatures();
    this.setFeatures(features);
  }

  IsEnabled(featureName: FeatureName) {
    return !!this.features?.find((feature) => feature.name === featureName)?.isEnabled;
  }

  get isCA3000Enabled() {
    return this.IsEnabled(FeatureName.CA3000);
  }

  get isCA4000Enabled() {
    return this.IsEnabled(FeatureName.CA4K);
  }

  get isGateControllerEnabled() {
    return this.isCA4000Enabled && this.IsEnabled(FeatureName.GateController);
  }

  get isEIQManageGateControllerEnabled() {
    return this.isCA4000Enabled && this.IsEnabled(FeatureName.EIQManageGateController);
  }

  get isCardAccessEnabled() {
    return this.IsEnabled(FeatureName.CA3000) || this.IsEnabled(FeatureName.CA4K);
  }

  get isCardAccessLogsImportEnabled() {
    return this.isCardAccessEnabled && this.IsEnabled(FeatureName.CA3KLogsImport);
  }

  get isEIQIntelliTicketEnabled() {
    return this.IsEnabled(FeatureName.EIQIntelliTicket);
  }

  get isEIQIntelliTicketEscalationTiersEnabled() {
    return this.isEIQIntelliTicketEnabled && this.IsEnabled(FeatureName.EIQIntelliTicketEscalationTiers);
  }

  get isEIQIntelliTicketConfirmPaymentEnabled() {
    return this.isEIQIntelliTicketEnabled && this.IsEnabled(FeatureName.EIQIntelliTicketConfirmPayment);
  }

  get isInsightEnabled() {
    return this.IsEnabled(FeatureName.Insight);
  }

  get isConnectEnabled() {
    return this.IsEnabled(FeatureName.Connect);
  }

  get isCardAccessExpirationsEnabled() {
    return this.isCardAccessEnabled && this.IsEnabled(FeatureName.CA3000Expirations);
  }

  get isCardAccessClassesEnabled() {
    return this.isCardAccessEnabled && this.IsEnabled(FeatureName.CA3000CardClasses);
  }

  get isNorthStar() {
    return this.IsEnabled(FeatureName.NorthStar);
  }

  get isFastAccessEnabled() {
    return this.IsEnabled(FeatureName.FastAccess);
  }

  get isFastAccessAlertsEnabled() {
    return this.IsEnabled(FeatureName.FastAccess) && this.IsEnabled(FeatureName.FastAccessAlerts);
  }

  get isFastAccessRestrictionsEnabled() {
    return this.IsEnabled(FeatureName.FastAccess) && this.IsEnabled(FeatureName.FastAccessRestrictions);
  }

  get isFastAccessPrintDirectionEnabled() {
    return this.IsEnabled(FeatureName.FastAccess) && this.IsEnabled(FeatureName.FastAccessPrintDirection);
  }

  get isSMSNotificationsEnabled() {
    return this.IsEnabled(FeatureName.SmsNotifications);
  }

  get isMassAlertsEnabled() {
    return this.IsEnabled(FeatureName.MassAlerts);
  }

  get isSMSMassAlertsEnabled() {
    return this.IsEnabled(FeatureName.MassAlerts) && this.IsEnabled(FeatureName.SmsMassAlerts);
  }

  get isIncidentReporterEnabled() {
    return this.IsEnabled(FeatureName.IncidentReporter);
  }

  get isDailyActivityReportEnabled() {
    return this.IsEnabled(FeatureName.IncidentReporter) && this.IsEnabled(FeatureName.DailyActivityReports);
  }

  get isIncidentReporterSendViaEmailEnabled() {
    return this.IsEnabled(FeatureName.IncidentReporter) && this.IsEnabled(FeatureName.IncidentReporterSendViaEmail);
  }

  get isPlateScanEnabled() {
    return this.IsEnabled(FeatureName.LPR);
  }

  get isRealTimeNotificationsEnabled() {
    return this.IsEnabled(FeatureName.LPR) || this.IsEnabled(FeatureName.EIQWhoIs) || this.IsEnabled(FeatureName.EIQLive);
  }

  get isGuestLimitationsEnabled() {
    return this.IsEnabled(FeatureName.GuestLimitations);
  }

  get isGuestLimitationsForAdminsEnabled() {
    return this.isGuestLimitationsEnabled && this.IsEnabled(FeatureName.GuestLimitationsForAdmins);
  }

  get isDriveIdEnabled() {
    return this.IsEnabled(FeatureName.DriveId);
  }

  get isSmartPlateIdEnabled() {
    return this.isCardAccessEnabled && this.IsEnabled(FeatureName.SmartPlateId);
  }

  get isEIQLiveEnabled() {
    return this.IsEnabled(FeatureName.EIQLive);
  }

  get isEIQProfileEnabled() {
    return this.IsEnabled(FeatureName.EIQProfile);
  }

  get isPayEaseEnabled() {
    return this.IsEnabled(FeatureName.PayEase);
  }

  get isRentalPortalEnabled() {
    return this.IsEnabled(FeatureName.RentalPortal);
  }

  get isJonasEnabled() {
    return this.IsEnabled(FeatureName.Jonas);
  }

  get isEIQAdvisoryEnabled() {
    return this.IsEnabled(FeatureName.EIQAdvisory);
  }
}

const featureStore = new FeatureStore();
export const FeatureStoreContext = React.createContext<FeatureStore>(featureStore);
export default featureStore;
